import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useListContext, useTranslate } from "react-admin";

import clsx from "clsx";
import { TextField as MuiTextField } from "@mui/material";
import { styled } from "@mui/material/styles";
import Autocomplete, { AutocompleteProps } from "@mui/material/Autocomplete";

const PREFIX = "RaAutocompleteInput";
const classes = {
  textField: `${PREFIX}-textField`,
};

const StyledAutocomplete = styled(Autocomplete as React.ComponentType<AutocompleteProps<Option, false, true, true>>)(
  ({ theme }) => ({
    [`& .${classes.textField}`]: {
      minWidth: theme.spacing(20),
      '& label:not([data-shrink="true"])': {
        transform: "translate(12px, 17px) scale(1)",
      },
    },
  })
);

interface Option {
  id: string;
  name: string;
}

// フィルター用の値を判定するヘルパー関数
const normalizeValue = (raw: string | Option | null, onlyNumeric: boolean): string => {
  let v = "";
  if (raw === null) {
    return v;
  }
  if (typeof raw === "object") {
    v = raw.id === "undecided" ? "undecided" : "";
  } else {
    v = raw === "未確定" ? "undecided" : raw;
  }
  if (onlyNumeric && v !== "undecided") {
    v = v.replace(/\D/g, "");
  }
  return v;
};

const useFilterAutocomplete = (
  source: string,
  onlyNumeric: boolean,
  filterValues: Record<string, any>,
  setFilters: (filters: Record<string, any>, debounce?: any) => void
) => {
  const [inputValue, setInputValue] = useState<string>("");

  useEffect(() => {
    const rawValue = filterValues[source];
    if (rawValue == null || rawValue === "") {
      setInputValue("");
      return;
    }
    setInputValue(rawValue === "undecided" ? "未確定" : String(rawValue));
  }, [filterValues, source]);

  const updateFilter = useCallback(
    (raw: string | Option | null) => {
      const value = normalizeValue(raw, onlyNumeric);
      setInputValue(value === "undecided" ? "未確定" : value);
      setFilters({ ...filterValues, [source]: value }, null);
    },
    [filterValues, onlyNumeric, setFilters, source]
  );

  const handleInputChange: AutocompleteProps<Option, false, true, true>["onInputChange"] = useCallback(
    (_: any, newValue: any, reason: any) => {
      if (reason === "input" || (reason === "reset" && newValue === "未確定")) {
        updateFilter(newValue);
      }
    },
    [updateFilter]
  );

  const handleOptionChange: AutocompleteProps<Option, false, true, true>["onChange"] = useCallback(
    (_: any, value: any) => {
      updateFilter(value);
    },
    [updateFilter]
  );

  return { inputValue, handleInputChange, handleOptionChange };
};

export const UndecidedOrFreeInput = (props: any) => {
  const { filterValues, setFilters } = useListContext();
  const { source, onlyNumeric = false } = props;
  const translate = useTranslate();
  const label = translate(`resources.adjustment_deals.fields.${source}`);
  const searchOptions = useMemo(() => [{ id: "undecided", name: "未確定" }], []);

  const { inputValue, handleInputChange, handleOptionChange } = useFilterAutocomplete(
    source,
    onlyNumeric,
    filterValues,
    setFilters
  );

  return (
    <StyledAutocomplete
      className={clsx("ra-input", `ra-input-${source}`)}
      freeSolo
      disableClearable
      options={searchOptions}
      getOptionLabel={(option: Option | string) => (typeof option === "string" ? option : option.name ?? "")}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleOptionChange}
      renderInput={(params) => (
        <MuiTextField
          {...params}
          label={label}
          inputProps={onlyNumeric ? params.inputProps : { ...params.inputProps, inputMode: "numeric", pattern: "\\d*" }}
          className={classes.textField}
          fullWidth={false}
        />
      )}
    />
  );
};
